import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  HelpCircle, 
  ChevronDown,
  ChevronUp,
  Search,
  MessageCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function FAQPage() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [openItems, setOpenItems] = useState({});

  const faqCategories = [
    {
      categoryKey: "faqPage.categories.general",
      questions: ["whatIsMegaReach", "isFree", "languages"]
    },
    {
      categoryKey: "faqPage.categories.millionCampaigns",
      questions: ["sendMillion", "telegramLimits", "buildSubscribers"]
    },
    {
      categoryKey: "faqPage.categories.integrations",
      questions: ["telegramToken", "botToChannel", "setupResend", "setupTwilio"]
    },
    {
      categoryKey: "faqPage.categories.abTesting",
      questions: ["whatIsABTest", "howManyVersions", "determineWinner"]
    },
    {
      categoryKey: "faqPage.categories.tracking",
      questions: ["trackEmailOpens", "openClickRates", "noTelegramStats"]
    },
    {
      categoryKey: "faqPage.categories.troubleshooting",
      questions: ["messagesNotSending", "botBanned", "emailSpam"]
    }
  ];

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredFaqs = faqCategories.map((category, catIndex) => ({
    ...category,
    categoryIndex: catIndex,
    questions: category.questions.filter(qKey => {
      const question = t(`faqPage.questions.${qKey}.q`);
      const answer = t(`faqPage.questions.${qKey}.a`);
      return question.toLowerCase().includes(searchTerm.toLowerCase()) ||
             answer.toLowerCase().includes(searchTerm.toLowerCase());
    })
  })).filter(category => category.questions.length > 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="faq-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-foreground">{t('faqPage.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('faqPage.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder={t('faqPage.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pe-12 text-lg py-6"
          data-testid="faq-search"
        />
      </div>

      {/* FAQ Categories */}
      <div className="space-y-6">
        {filteredFaqs.map((category) => (
          <Card key={category.categoryIndex} data-testid={`faq-category-${category.categoryIndex}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                {t(category.categoryKey)}
                <Badge variant="outline">{category.questions.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {category.questions.map((qKey, questionIndex) => {
                const key = `${category.categoryIndex}-${questionIndex}`;
                const isOpen = openItems[key];
                return (
                  <div 
                    key={questionIndex} 
                    className="border border-border rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleItem(category.categoryIndex, questionIndex)}
                      className="w-full p-4 text-start flex items-center justify-between hover:bg-muted/50 transition-colors"
                      data-testid={`faq-item-${category.categoryIndex}-${questionIndex}`}
                    >
                      <span className="font-medium">{t(`faqPage.questions.${qKey}.q`)}</span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="p-4 pt-0 text-muted-foreground border-t border-border bg-muted/30">
                        {t(`faqPage.questions.${qKey}.a`)}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Still have questions */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="text-xl font-bold mb-2">{t('faqPage.noAnswerFound')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('faqPage.contactUs')}
            </p>
            <div className="flex justify-center gap-4">
              <a 
                href="https://t.me/support" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              >
                {t('faqPage.contactViaTelegram')}
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
